import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@/services/adminConsoleService';
import type { AddResponse, SecurityGroup } from '@/services/adminConsoleService';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';

// ─── CustomSelect ─────────────────────────────────────────────────────────────

interface SelectOption {
  value: number | string;
  label: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: number | string | null;
  placeholder: string;
  disabled?: boolean;
  onChange: (value: number | null) => void;
  maxHeight?: number;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  placeholder,
  disabled = false,
  onChange,
  maxHeight = 200,
}) =>
{
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() =>
  {
    if (!open) return;
    const handler = (e: MouseEvent) =>
    {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} className="relative flex-1">
      <button
        type="button"
        disabled={disabled}
        className={`w-full h-[32px] flex items-center justify-between border border-gray-300 dark:border-[#4a4c4c] rounded px-2 text-sm bg-white dark:bg-[#3a3c3c] text-left ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:border-gray-400 dark:hover:border-[#62aacb]'
        }`}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span
          className={
            selected ? 'text-gray-700 dark:text-[#a9a093]' : 'text-gray-400 dark:text-[#6e6860]'
          }
        >
          {selected ? selected.label : placeholder}
        </span>
        <span
          className="material-icons text-gray-400 dark:text-[#6e6860]"
          style={{ fontSize: '16px' }}
        >
          arrow_drop_down
        </span>
      </button>

      {open && (
        <ul
          className="absolute z-50 w-full bg-white dark:bg-[#3a3c3c] border border-gray-300 dark:border-[#4a4c4c] rounded shadow-md mt-0.5 overflow-y-auto"
          style={{ maxHeight }}
        >
          <li
            className="px-3 py-1.5 text-sm text-gray-400 dark:text-[#6e6860] hover:bg-gray-100 dark:hover:bg-[#333535] cursor-pointer"
            onMouseDown={() =>
            {
              onChange(null);
              setOpen(false);
            }}
          >
            {placeholder}
          </li>
          {options.map(opt => (
            <li
              key={opt.value}
              className={`px-3 py-1.5 text-sm cursor-pointer hover:bg-[#e8f4f8] dark:hover:bg-[#333535] ${
                opt.value === value
                  ? 'bg-[#d0eaf5] dark:bg-[#1a3a4a] text-gray-800 dark:text-[#a9a093]'
                  : 'text-gray-700 dark:text-[#a9a093]'
              }`}
              onMouseDown={() =>
              {
                onChange(opt.value as number);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectedSubGroup {
  groupId: number;
  groupName: string;
  subGroupId: number;
  subGroupName: string;
}

interface PersonBlock {
  id: string;
  emails: string[];
  inputValue: string;
  emailError: boolean;
  selectedSubGroups: SelectedSubGroup[];
  currentGroupId: number | null;
  currentSubGroupId: number | null;
  defaultGroupId: number | null;
  csvFile: File | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

let blockCounter = 0;
const newBlock = (): PersonBlock => ({
  id: `block-${++blockCounter}`,
  emails: [],
  inputValue: '',
  emailError: false,
  selectedSubGroups: [],
  currentGroupId: null,
  currentSubGroupId: null,
  defaultGroupId: null,
  csvFile: null,
});

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

// ─── InviteNewUsers ───────────────────────────────────────────────────────────

export interface InviteNewUsersProps {
  onCancel: () => void;
  onSuccess: () => void;
  asPage?: boolean;
}

export const InviteNewUsers: React.FC<InviteNewUsersProps> = ({
  onCancel,
  onSuccess,
  asPage = false,
}) =>
{
  const [blocks, setBlocks] = useState<PersonBlock[]>([newBlock()]);
  const [securityGroups, setSecurityGroups] = useState<SecurityGroup[]>([]);
  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  const [userLimit, setUserLimit] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [_failedEmails, setFailedEmails] = useState<string[]>([]);
  const [showErrorPanel, setShowErrorPanel] = useState(false);
  const [addResponse, setAddResponse] = useState<AddResponse>();

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() =>
  {
    Promise.all([
      adminConsoleService.getSecurityGroups(),
      adminConsoleService.getLicense().catch(() => null),
      adminConsoleService.getAdminUsers().catch(() => []),
    ]).then(([groups, license, users]) =>
    {
      setSecurityGroups(groups);
      if (license) setUserLimit(license.TotalUsers);
      if (users) setActiveUsers(users.filter(u => u.IsEnabled).length);
    });
  }, []);

  const updateBlock = useCallback((id: string, patch: Partial<PersonBlock>) =>
  {
    setBlocks(prev => prev.map(b => (b.id === id ? { ...b, ...patch } : b)));
  }, []);

  const commitEmail = (blockId: string, block: PersonBlock) =>
  {
    const raw = block.inputValue.trim().replace(/,$/, '');
    if (!raw) return;
    if (!isValidEmail(raw))
    {
      updateBlock(blockId, { emailError: true });
      return;
    }
    updateBlock(blockId, {
      emails: [...block.emails, raw.toLowerCase()],
      inputValue: '',
      emailError: false,
    });
  };

  const handleEmailKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    blockId: string,
    block: PersonBlock,
  ) =>
  {
    if (e.key === 'Enter' || e.key === ',')
    {
      e.preventDefault();
      commitEmail(blockId, block);
    }
    else if (e.key === 'Backspace' && !block.inputValue && block.emails.length > 0)
    {
      updateBlock(blockId, { emails: block.emails.slice(0, -1) });
    }
    else
    {
      updateBlock(blockId, { emailError: false });
    }
  };

  const removeEmail = (blockId: string, block: PersonBlock, index: number) =>
  {
    updateBlock(blockId, { emails: block.emails.filter((_, i) => i !== index) });
  };

  const handleCsvChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    blockId: string,
    block: PersonBlock,
  ) =>
  {
    const file = e.target.files?.[0];
    if (!file) return;
    updateBlock(blockId, { csvFile: file });
    const reader = new FileReader();
    reader.onload = ev =>
    {
      const text = ev.target?.result as string;
      const extracted = text
        .split(/[\s,;]+/)
        .map(s => s.trim().toLowerCase())
        .filter(isValidEmail);
      const merged = Array.from(new Set([...block.emails, ...extracted]));
      updateBlock(blockId, { emails: merged, emailError: false });
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadSample = () =>
  {
    const content = 'sample1@email.com, sample2@email.com,sample3@email.com,sample4@email.com';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sampleInvitations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGroupChange = (blockId: string, groupId: number | null) =>
  {
    updateBlock(blockId, { currentGroupId: groupId, currentSubGroupId: null });
  };

  const handleSubGroupChange = (blockId: string, block: PersonBlock, subGroupId: number | null) =>
  {
    updateBlock(blockId, { currentSubGroupId: subGroupId });
    if (subGroupId == null || block.currentGroupId == null) return;

    const group = securityGroups.find(g => g.GroupId === block.currentGroupId);
    const subGroup = group?.SecurityGroups.find(sg => sg.SecurityGroupID === subGroupId);
    if (!group || !subGroup) return;

    if (block.selectedSubGroups.some(s => s.subGroupId === subGroupId)) return;

    updateBlock(blockId, {
      selectedSubGroups: [
        ...block.selectedSubGroups,
        {
          groupId: group.GroupId,
          groupName: group.Name,
          subGroupId: subGroup.SecurityGroupID,
          subGroupName: subGroup.Name,
        },
      ],
      currentGroupId: null,
      currentSubGroupId: null,
    });
  };

  const removeSubGroup = (blockId: string, block: PersonBlock, subGroupId: number) =>
  {
    updateBlock(blockId, {
      selectedSubGroups: block.selectedSubGroups.filter(s => s.subGroupId !== subGroupId),
    });
  };

  const handleSend = async () =>
  {
    const requestObj: { TenancyName: string; People: any[] } = {
      TenancyName: CorgeeStorageHelper.getTenantData().TenancyName,
      People: [],
    };

    for (let i = 0; i < blocks.length; i++)
    {
      const form = blocks[i]!;
      const securityGroup = securityGroups.find(x => x.GroupId === form.defaultGroupId);
      const firstSubGroup = securityGroup?.SecurityGroups[0];
      requestObj.People.push({
        EmailList: form.emails.join(','),
        SecurityGroups: form.selectedSubGroups.map(x =>
        {
          return {
            GroupId: x.groupId,
            Name: x.subGroupName,
            SecurityGroupId: x.subGroupId,
            GroupName: x.groupName,
          };
        }),
        DefaultGroup: {
          GroupId: firstSubGroup?.GroupId,
          Name: firstSubGroup?.Name,
          Description: firstSubGroup?.Description ?? null,
          IsDeleted: firstSubGroup?.IsDeleted ?? false,
          IsDefault: (firstSubGroup as any)?.IsDefault ?? false,
          GroupPersons: (firstSubGroup as any)?.GroupPersons ?? null,
          GroupLayers:
            firstSubGroup?.SecurityGroupLayers?.map(x =>
            {
              return {
                GroupId: firstSubGroup?.GroupId,
                LayerId: x.LayerId,
                Name: x.Name,
                Color: x.Color,
                IsEnabled: false,
                IsDefault: false,
              };
            }) ?? [],
          GroupSecurityGroups:
            securityGroup?.SecurityGroups.filter(
              x => !form.selectedSubGroups.map(y => y.subGroupId).includes(x.SecurityGroupID),
            ).map(x =>
            {
              return {
                GroupId: x.GroupId,
                Name: x.Name,
                SecurityGroupId: x.SecurityGroupID,
              };
            }) ?? [],
        },
      });
    }

    setLoading(true);
    setFailedEmails([]);
    setShowErrorPanel(false);

    try
    {
      const result = await adminConsoleService.inviteUsers(requestObj);
      console.log(result);

      setAddResponse(result);
      setFailedEmails(result.noCreatedAccounts);
      setBlocks([newBlock()]);
      setShowErrorPanel(true);
      onSuccess();

      // if(result?.estatus === false) {
      // }
      // if (result?.FailedEmails?.length > 0) {
      //   setShowErrorPanel(true);
      // } else {
      //   //onSuccess();
      // }
    }
    catch
    {
      // setShowErrorPanel(true);
    }
    finally
    {
      setLoading(false);
    }
  };

  const subGroupsForBlock = (block: PersonBlock) =>
  {
    if (block.currentGroupId == null) return [];
    block.selectedSubGroups;
    return (
      securityGroups
        .find(g => g.GroupId === block.currentGroupId)
        ?.SecurityGroups.filter(
          x => !block.selectedSubGroups.map(y => y.subGroupId).includes(x.SecurityGroupID),
        ) ?? []
    );
  };

  const panel = (
    <div
      className={
        asPage
          ? 'bg-white dark:bg-[#2d2f2f] border border-[#ddd] dark:border-[#4a4c4c] rounded w-full max-w-[720px] flex flex-col'
          : 'bg-[#ededed] dark:bg-[#2d2f2f] rounded shadow-2xl w-[720px] max-h-[90vh] flex flex-col overflow-hidden'
      }
      onClick={asPage ? undefined : e => e.stopPropagation()}
    >
      {/* Header — only in modal mode */}
      {!asPage && (
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-[#2d2f2f] border-b border-[#e0e0e0] dark:border-[#4a4c4c]">
          <h2 className="text-[#62aacb] text-lg font-normal">Invite new users</h2>
          <button onClick={onCancel} className="text-[#aaa] hover:text-[#555]">
            <span className="material-icons text-[20px]">close</span>
          </button>
        </div>
      )}

      {/* Scrollable body */}
      <div className={asPage ? 'flex-1 p-6' : 'flex-1 overflow-y-auto p-6'}>
        {/* Error panel */}
        {showErrorPanel && (
          <div className="bg-white dark:bg-[#2d2f2f] rounded shadow mb-4 relative">
            <button
              className="absolute top-2 right-3 text-gray-400 dark:text-[#6e6860] hover:text-gray-600 dark:hover:text-[#a9a093] text-lg"
              onClick={() => setShowErrorPanel(false)}
            >
              ×
            </button>
            {addResponse?.createdAccounts && addResponse.createdAccounts.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-[#1a3a1a] border-b border-green-100 dark:border-[#2a5a2a] rounded-t">
                  <span className="text-green-500 text-sm font-medium">✓</span>
                  <span className="text-sm text-gray-700 dark:text-[#a9a093]">
                    {`${addResponse.createdAccounts.length} invitations have been sent successfully.`}
                  </span>
                </div>
              </>
            )}
            {addResponse?.noCreatedAccounts && addResponse.noCreatedAccounts.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-[#3a1a1a] border-b border-red-100 dark:border-[#5a3535] rounded-t">
                  <span className="text-red-500 text-sm font-medium">✕</span>
                  <span className="text-sm text-gray-700 dark:text-[#a9a093]">
                    The following email(s) will not receive the invitation.
                  </span>
                </div>
                <div className="px-4 py-3 text-sm text-gray-600 dark:text-[#a9a093] bg-red-50 dark:bg-[#3a1a1a]">
                  {addResponse?.noCreatedAccounts.map(email => (
                    <span key={email} className="mr-1">
                      {email};
                    </span>
                  ))}
                </div>
              </>
            )}
            {addResponse?.estatus === false && (
              <>
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-[#3a1a1a] border-b border-red-100 dark:border-[#5a3535] rounded-t">
                  <span className="text-red-500 text-sm font-medium">✕</span>
                  <span className="text-sm text-gray-700 dark:text-[#a9a093]">
                    Error sending invitation to the email(s). Please contact your administrator.
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Person blocks */}
        {blocks.map((block, blockIndex) => (
          <div
            key={block.id}
            className={
              asPage
                ? `relative px-5 py-4 ${blockIndex < blocks.length - 1 ? 'border-b border-[#eee] dark:border-[#4a4c4c]' : ''}`
                : 'bg-white dark:bg-[#333535] rounded shadow mb-4 relative'
            }
          >
            {blocks.length > 1 && (
              <button
                className="absolute top-2 right-3 text-gray-400 dark:text-[#6e6860] hover:text-gray-600 dark:hover:text-[#a9a093] text-lg"
                onClick={() => setBlocks(prev => prev.filter(b => b.id !== block.id))}
              >
                ×
              </button>
            )}

            <div className={asPage ? '' : 'px-5 py-4'}>
              {/* Header row */}
              <div className="flex items-start justify-between mb-3">
                <span className="text-sm text-gray-700 dark:text-[#a9a093]">
                  Add the email addresses of the users you want to invite
                </span>
                {blockIndex === 0 && activeUsers !== null && userLimit !== null && (
                  <span className="text-sm text-gray-600 dark:text-[#a9a093] whitespace-nowrap ml-4">
                    {activeUsers} Active Users of {userLimit} Users Limit
                  </span>
                )}
              </div>

              {/* Email tag input */}
              <div
                className="border border-gray-300 dark:border-[#4a4c4c] rounded px-2 py-2 min-h-[70px] flex flex-wrap gap-1 cursor-text mb-1 dark:bg-[#3a3c3c]"
                onClick={() => document.getElementById(`email-input-${block.id}`)?.focus()}
              >
                {block.emails.map((email, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1 bg-gray-100 dark:bg-[#4a4c4c] border border-gray-300 dark:border-[#555] rounded px-2 py-0.5 text-xs text-gray-700 dark:text-[#a9a093]"
                  >
                    {email}
                    <button
                      className="text-gray-400 dark:text-[#6e6860] hover:text-gray-600 dark:hover:text-[#a9a093] ml-1"
                      onClick={e =>
                      {
                        e.stopPropagation();
                        removeEmail(block.id, block, i);
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id={`email-input-${block.id}`}
                  type="text"
                  className="flex-1 min-w-[200px] outline-none text-sm text-gray-700 dark:text-[#a9a093] placeholder-gray-400 dark:placeholder-[#6e6860] bg-transparent"
                  placeholder={
                    block.emails.length === 0
                      ? 'Type or paste an email and press Enter after each one...'
                      : ''
                  }
                  value={block.inputValue}
                  onChange={e =>
                    updateBlock(block.id, { inputValue: e.target.value, emailError: false })
                  }
                  onKeyDown={e => handleEmailKeyDown(e, block.id, block)}
                  onBlur={() => commitEmail(block.id, block)}
                />
              </div>

              {block.emailError && (
                <div className="bg-[#e8f4f8] dark:bg-[#1a3a4a] text-right px-3 py-1 text-xs text-[#62aacb] mb-2 rounded">
                  Incorrect email format.
                </div>
              )}

              {/* CSV row */}
              <div className="flex items-center gap-3 mt-3 mb-4">
                <span className="text-sm text-gray-700 dark:text-[#a9a093]">
                  Select a csv file:
                </span>
                <button
                  className="border border-gray-400 dark:border-[#4a4c4c] rounded px-3 py-1 text-sm bg-white dark:bg-[#466b81] dark:text-[#a9a093] hover:bg-gray-50 dark:hover:bg-[#444646]"
                  onClick={() => fileInputRefs.current[block.id]?.click()}
                >
                  Browse
                </button>
                <button className="text-[#62aacb] text-sm hover:underline" onClick={downloadSample}>
                  CSV sample download
                </button>
                <input
                  ref={el =>
                  {
                    fileInputRefs.current[block.id] = el;
                  }}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={e => handleCsvChange(e, block.id, block)}
                />
              </div>

              {/* Security Groups */}
              <div className="flex items-start gap-4 mb-3">
                <div className="flex-1">
                  <p className="text-sm text-gray-700 dark:text-[#a9a093] font-medium mb-1">
                    Select security groups for these users
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#a9a093] mb-2">
                    Users will have access to these security groups.
                  </p>
                  {block.selectedSubGroups.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {block.selectedSubGroups.map(sg => (
                        <span
                          key={sg.subGroupId}
                          className="flex items-center gap-1 bg-[#e8f4f8] dark:bg-[#1a3a4a] border border-[#b8d8e8] dark:border-[#3c6c80] rounded px-2 py-0.5 text-xs text-gray-700 dark:text-[#a9a093]"
                        >
                          {sg.groupName}: {sg.subGroupName}
                          <button
                            className="text-gray-400 dark:text-[#6e6860] hover:text-gray-600 dark:hover:text-[#a9a093] ml-1"
                            onClick={() => removeSubGroup(block.id, block, sg.subGroupId)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <CustomSelect
                      placeholder="Security Group"
                      value={block.currentGroupId}
                      options={securityGroups.map(g => ({ value: g.GroupId, label: g.Name }))}
                      onChange={val => handleGroupChange(block.id, val)}
                      maxHeight={200}
                    />
                    <CustomSelect
                      placeholder="Security Sub-Group"
                      value={block.currentSubGroupId}
                      disabled={block.currentGroupId == null}
                      options={subGroupsForBlock(block).map(sg => ({
                        value: sg.SecurityGroupID,
                        label: sg.Name,
                      }))}
                      onChange={val => handleSubGroupChange(block.id, block, val)}
                      maxHeight={200}
                    />
                  </div>
                </div>
              </div>

              {/* Default Security Group */}
              <div className="flex items-start gap-4">
                <div className="flex-1 max-w-xs">
                  <p className="text-sm text-gray-700 dark:text-[#a9a093] font-medium mb-1">
                    Select a default security group for these users
                  </p>
                  <p className="text-xs text-gray-500 dark:text-[#a9a093] mb-2">
                    New cards added by these users will default to this security group.{' '}
                    <span className="italic">
                      (If they have access to multiple security groups and do not select a security
                      group when creating a card).
                    </span>
                  </p>
                  <CustomSelect
                    placeholder="Default Group"
                    value={block.defaultGroupId}
                    options={securityGroups
                      .filter(x => block.selectedSubGroups.map(y => y.groupId).includes(x.GroupId))
                      .map(g => ({ value: g.GroupId, label: g.Name }))}
                    onChange={val => updateBlock(block.id, { defaultGroupId: val })}
                    maxHeight={200}
                  />
                </div>
              </div>
            </div>

            {!asPage && blockIndex === blocks.length - 1 && (
              <div className="px-5 py-3 border-t border-gray-100 dark:border-[#4a4c4c]">
                <button
                  className="flex items-center gap-1 text-[#62aacb] text-sm"
                  onClick={() => setBlocks(prev => [...prev, newBlock()])}
                >
                  <span className="text-lg leading-none">⊕</span>
                  <span className="hover:underline">Add more people</span>
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add more people — page mode only, outside blocks */}
        {asPage && (
          <div className="px-5 py-3 border-t border-[#eee] dark:border-[#4a4c4c]">
            <button
              className="flex items-center gap-1 text-[#62aacb] text-sm hover:underline"
              onClick={() => setBlocks(prev => [...prev, newBlock()])}
            >
              <span className="text-lg leading-none">⊕</span>
              Add more people
            </button>
          </div>
        )}

        {/* Action buttons — modal mode only */}
        {!asPage && (
          <div className="flex justify-end gap-2 mt-2">
            <button
              className="px-4 py-1.5 text-sm bg-gray-400 dark:bg-[#555] text-white rounded hover:bg-gray-500 dark:hover:bg-[#666]"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 text-sm text-white rounded disabled:bg-gray-400 disabled:opacity-50 bg-[#62aacb] hover:bg-[#4a96ba]"
              onClick={handleSend}
              disabled={loading || blocks.every(b => b.emails.length === 0)}
            >
              {loading ? 'Sending...' : 'Send Invitations'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (asPage)
  {
    return (
      <div className="p-5 max-w-[760px]">
        <h2 className="text-[#62aacb] text-xl font-normal mb-4">Invite new users</h2>
        {panel}
        <div className="flex justify-end gap-2 mt-3">
          <button
            className="px-4 py-1.5 text-sm bg-gray-400 dark:bg-[#555] text-white rounded hover:bg-gray-500 dark:hover:bg-[#666]"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:bg-gray-400 bg-[#62aacb] hover:bg-[#4a96ba]"
            onClick={handleSend}
            disabled={loading || blocks.every(b => b.emails.length === 0)}
          >
            {loading ? 'Sending...' : 'Send Invitations'}
          </button>
        </div>
      </div>
    );
  }

  return createPortal(
    <div
      className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
      onClick={onCancel}
    >
      {panel}
    </div>,
    document.body,
  );
};
